import useIsMountedRef from "Common/componentUtils/useIsMountedRef";
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Transition } from "react-transition-group";
function SlideUpDown(props) {
    const container = useRef(null);
    const details = useRef(null);
    const isMountedRef = useIsMountedRef();
    const [wasOpen, setWasOpen] = useState(() => !props.mountOnlyAfterFirstOpening || props.open);
    const timerRef = useRef(null);
    const getMaxHeight = () => container.current ? Math.floor(container.current.getBoundingClientRect().height) : 0;
    useEffect(() => () => {
        timerRef.current && clearTimeout(timerRef.current);
    }, []);
    useEffect(() => {
        !wasOpen && props.open && setWasOpen(true);
    }, [props.open]);
    useLayoutEffect(() => {
        if (wasOpen && !props.open && details.current) {
            details.current.style.maxHeight = `${getMaxHeight()}px`;
        }
    }, [props.open]);
    const handleEntered = useCallback((node) => {
        if (!props.onAnimationCompleted)
            return;
        props.onAnimationCompleted(node);
    }, [props.onAnimationCompleted]);
    const handleExisted = useCallback((node) => {
        if (!props.onAnimationCompleted)
            return;
        timerRef.current && clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            props.onAnimationCompleted && props.onAnimationCompleted(node);
        }, props.animationDuration);
    }, [props.onAnimationCompleted, props.animationDuration]);
    const handleEntering = useCallback((node, isAppearing) => {
        props.onAnimationStarted && props.onAnimationStarted(node, isAppearing);
    }, [props.onAnimationStarted]);
    const handleExisting = handleEntering;
    const timeout = useMemo(() => ({
        appear: 0,
        enter: props.animationDuration,
        exit: props.animationDuration
    }), [props.animationDuration]);
    const getStyle = useCallback((open, state) => {
        const style = {
            ...(props.style || {}),
            minHeight: `${props.closedMinHeight}px`,
            transition: `max-height ${props.animationDuration}ms ease-in-out`
        };
        if (open) {
            if (state === "exited") {
                style.maxHeight = `${props.closedMinHeight}px`;
                style.overflow = "hidden";
            }
            if (state === "entering") {
                style.maxHeight = `${getMaxHeight()}px`;
                style.overflow = "hidden";
            }
        }
        else {
            style.overflow = "hidden";
            if (state === "entered") {
                style.maxHeight = `${getMaxHeight()}px`;
            }
            if (state === "exited" || state === "exiting") {
                style.maxHeight = `${props.closedMinHeight}px`;
            }
        }
        return style;
    }, [props.closedMinHeight, props.animationDuration, props.style]);
    const customWrapStyles = useMemo(() => (props.openedHeight ? { height: props.openedHeight } : {}), [props.openedHeight]);
    return (React.createElement(Transition, { in: props.open, timeout: !isMountedRef.current && props.open ? 0 : timeout, onEntered: handleEntered, onExited: handleExisted, onEntering: handleEntering, onExisting: handleExisting, mountOnEnter: wasOpen, unmountOnExit: props.unmountOnExit }, (state) => (React.createElement("div", { ref: details, style: getStyle(props.open, state), className: props.className },
        React.createElement("div", { ref: container, className: props.customWrap, style: customWrapStyles }, (wasOpen || props.open) && props.children)))));
}
export default memo(SlideUpDown);
SlideUpDown.defaultProps = {
    closedMinHeight: 0,
    animationDuration: 400,
    mountOnlyAfterFirstOpening: true
};
